import { ApiError } from '../api/client'
import { fetchMe } from '../api/auth'
import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, Divider, StatCard, StatGrid, StatusPill, TextField } from './ui'
import {
  treasuryApi,
  type DistributionScenario,
  type TreasurySettings,
  type TreasuryWaterfall,
  type WaterfallStep,
} from '../api/treasury'

/**
 * F-152 / TASK-0406 (REQ-160-001, REQ-159-001, REQ-161-001, REQ-197-001) — la cascata di liquidità: dal saldo finanziario
 * di conti alla liquidità libera gestionale, passo per passo e con la fonte di ogni cifra. Una proiezione deterministica, non
 * una previsione statistica. Tutti i calcoli stanno sul server: qui si mostrano.
 *
 * <b>Riserva minima</b>: è una configurazione, non un movimento. La imposta solo l'amministrazione (il server risponde 403
 * agli altri); valori negativi sono rifiutati.
 *
 * <b>Scenario di distribuzione</b>: lo vede solo l'Admin (per gli altri la richiesta riceve 403 e la sezione non compare).
 * Mostra sempre l'etichetta obbligatoria: è una disponibilità gestionale potenziale, NON un utile legalmente distribuibile.
 *
 * Componenti M3 (Card, DataTable plain, StatCard, TextField, Callout). Si monta dietro il flag `finanza.cascata_liquidita`.
 */

const REASON: Record<string, string> = {
  disabled: "L'integrazione con conti non è attiva.",
  not_configured: "L'integrazione con conti non è configurata.",
  timeout: 'Conti non ha risposto in tempo.',
  unreachable: 'Conti non è raggiungibile.',
  unauthorized: 'Conti ha rifiutato le credenziali del CRM.',
  invalid_response: 'Conti ha risposto con dati non leggibili.',
  error: 'Conti ha risposto con un errore.',
  partners_disabled: 'La lettura dei soci da conti non è attiva.',
}

const SOURCE_LABEL: Record<string, string> = { conti: 'conti', crm: 'CRM', configurazione: 'configurazione' }

function eur(n: number | null | undefined): string {
  return n == null ? '—' : '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function sign(step: WaterfallStep): string {
  return step.effect === 'subtract' ? '− ' : ''
}

// Ruoli che passano PolicyNames.FinanceAdministration sul server (la scrittura della riserva).
const FINANCE_ADMIN_WRITE_ROLES = ['Administrator', 'EqualAdmin', 'EqualAmministrazione']

export function TreasuryWaterfallPanel() {
  const [canEditReserve, setCanEditReserve] = useState(false)
  const [data, setData] = useState<TreasuryWaterfall | null>(null)
  const [settings, setSettings] = useState<TreasurySettings | null>(null)
  const [scenario, setScenario] = useState<DistributionScenario | null>(null)
  const [reserve, setReserve] = useState('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setError('')
    treasuryApi.waterfall()
      .then(setData)
      .catch(e => { setData(null); setError(e instanceof Error ? e.message : 'Errore durante il caricamento della cascata') })
    treasuryApi.settings()
      .then(s => { setSettings(s); setReserve(String(s.minimumReserve)) })
      .catch(() => setSettings(null))
    // Lo scenario è solo per l'Admin: per gli altri la richiesta riceve 403 e la sezione non compare.
    treasuryApi.distributionScenario().then(setScenario).catch(() => setScenario(null))
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    let alive = true
    fetchMe()
      .then(me => { if (alive) setCanEditReserve(!!me && FINANCE_ADMIN_WRITE_ROLES.includes(me.role)) })
      .catch(() => { if (alive) setCanEditReserve(false) })
    return () => { alive = false }
  }, [])

  async function saveReserve(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSaved('')
    const value = Number(reserve.replace(',', '.'))
    if (!Number.isFinite(value) || value < 0) {
      setError('La riserva minima deve essere un importo maggiore o uguale a zero.')
      return
    }
    setSaving(true)
    try {
      await treasuryApi.saveSettings({ minimumReserve: value })
      setSaved('Riserva minima salvata.')
      load()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante il salvataggio'
      setError(err instanceof ApiError && err.status === 403 ? "Solo l'amministrazione può modificare la riserva minima." : message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Cascata di liquidità" />
      <CardContent>
        <div className="md-stack">
          {error && <Callout tone="error">{error}</Callout>}
          {saved && <Callout tone="success">{saved}</Callout>}

          {data && !data.available && (
            <Callout tone="warning">
              {REASON[data.reason ?? ''] ?? data.message ?? 'Conti non è disponibile.'} Il saldo e i totali non sono calcolabili; restano le voci del CRM.
            </Callout>
          )}

          {data && (
            <>
              <StatGrid min={240} max={2}>
                <StatCard
                  label="Al netto dei debiti maturati e degli impegni"
                  value={eur(data.netOfAccruals)}
                  supporting={`Passi 2–7, entro ${data.days} giorni (scaduti compresi)`}
                />
                <StatCard
                  label="Liquidità libera gestionale"
                  value={eur(data.freeLiquidity)}
                  supporting={`Dopo la riserva minima di ${eur(data.minimumReserve)}`}
                />
              </StatGrid>

              <DataTable variant="plain" aria-label="Cascata di liquidità">
                <thead>
                  <tr><th>Passo</th><th>Voce</th><th>Fonte</th><th className="md-num">Importo</th><th className="md-num">Dopo il passo</th></tr>
                </thead>
                <tbody>
                  {data.steps.map(s => (
                    <Fragment key={s.step}>
                      <tr>
                        <td>{s.step}</td>
                        <td className={s.effect === 'result' ? 'md-typescale-emphasized-body-medium' : undefined}>
                          {s.label}
                          {s.count > 0 && <span className="text-muted"> ({s.count})</span>}
                          {s.note && <div className="md-typescale-body-small text-muted">{s.note}</div>}
                        </td>
                        <td><StatusPill tone="neutral">{SOURCE_LABEL[s.source] ?? s.source}</StatusPill></td>
                        <td className="md-num">{s.amount == null ? '—' : sign(s) + eur(s.amount)}</td>
                        <td className="md-num">{eur(s.running)}</td>
                      </tr>
                      {s.details.filter(d => !d.counted).map(d => (
                        <tr key={`${s.step}-${d.label}`} className="text-muted">
                          <td />
                          <td>{d.label} — solo dettaglio, non sommato</td>
                          <td><StatusPill tone="neutral">{SOURCE_LABEL[d.source] ?? d.source}</StatusPill></td>
                          <td className="md-num">{eur(d.amount)}</td>
                          <td />
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </DataTable>
              <div className="md-typescale-body-small text-muted">
                Proiezione deterministica ({data.method}), non una previsione statistica. Le imposte stimate sono quelle calcolate da conti: il CRM non fa scelte fiscali.
              </div>
            </>
          )}

          {canEditReserve && (
            <form className="md-stack" onSubmit={saveReserve}>
              <div className="md-cluster">
                <TextField
                  id="treasury-reserve"
                  label="Riserva minima (€)"
                  type="number"
                  min={0}
                  step="0.01"
                  value={reserve}
                  onChange={e => setReserve(e.target.value)}
                  disabled={!settings}
                />
                <Button type="submit" variant="tonal" disabled={saving || !settings}>Salva riserva</Button>
              </div>
              <div className="md-typescale-body-small text-muted">È una configurazione, non un movimento: la modifica è riservata all'amministrazione.</div>
            </form>
          )}

          {scenario && (
            <>
              <Divider />
              <section className="md-stack" aria-label="Scenario di distribuzione">
                <h3 className="md-typescale-title-small m-0">Scenario di distribuzione</h3>
                <Callout tone="warning">{scenario.label}</Callout>
                {!scenario.available ? (
                  <div className="md-typescale-body-small text-muted">
                    {REASON[scenario.reason ?? ''] ?? scenario.message ?? 'Scenario non disponibile.'}
                  </div>
                ) : (
                  <>
                    <DataTable variant="plain" aria-label="Scenario di distribuzione">
                      <thead><tr><th>Socio</th><th className="md-num">Quota</th><th className="md-num">Importo</th></tr></thead>
                      <tbody>
                        {(scenario.shares ?? []).map(s => (
                          <tr key={s.partnerId}>
                            <td>{s.name}</td>
                            <td className="md-num">{(s.quota * 100).toLocaleString('it-IT', { maximumFractionDigits: 2 })}%</td>
                            <td className="md-num">{eur(s.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                    {scenario.conti && (
                      <div className="md-typescale-body-small text-muted">
                        Confronto con conti ({scenario.conti.label}): disponibile {eur(scenario.conti.available)}, tasse da coprire {eur(scenario.conti.taxesToCover)}, da dare a terzi {eur(scenario.conti.owedToThirdParties)}.
                      </div>
                    )}
                    <div className="md-typescale-body-small text-muted">{scenario.note}</div>
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
